import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { ApiKeyRecord, apiErrorMessage, teamService, TeamMember } from '@/services/team-service'
import * as toast from '@/lib/toast'
import { CreateApiKeyDialog } from './CreateApiKeyDialog'

interface ApiKeysPanelProps {
  keys: ApiKeyRecord[]
  agents: TeamMember[]
  onChanged: () => void
}

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString('pt-BR') : '—')

/** Chaves de API do agente: listar, criar (mostra uma vez) e revogar. */
export function ApiKeysPanel({ keys, agents, onChanged }: ApiKeysPanelProps) {
  const revoke = async (key: ApiKeyRecord) => {
    try {
      await teamService.revokeApiKey(key.id)
      toast.success(`Chave "${key.label}" revogada`)
      onChanged()
    } catch (error) {
      toast.error(apiErrorMessage(error))
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {agents.length === 0
            ? 'Nenhum usuário com papel Agente. Defina o papel na aba Equipe.'
            : 'O agente entra só por chave (header X-Api-Key); não faz login de tela.'}
        </p>
        <CreateApiKeyDialog agents={agents} onCreated={onChanged} />
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Agente</TableHead>
            <TableHead>Prefixo</TableHead>
            <TableHead>Criada</TableHead>
            <TableHead>Último uso</TableHead>
            <TableHead className="text-right">Situação</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {keys.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">Nenhuma chave criada</TableCell>
            </TableRow>
          )}
          {keys.map((key) => (
            <TableRow key={key.id}>
              <TableCell className="font-medium">{key.label}</TableCell>
              <TableCell>{key.userName}</TableCell>
              <TableCell className="font-mono text-xs">lt_{key.prefix}_…</TableCell>
              <TableCell>{formatDate(key.createdAt)}</TableCell>
              <TableCell>{formatDate(key.lastUsedAt)}</TableCell>
              <TableCell className="text-right">
                {key.revokedAt ? (
                  <Badge variant="outline">Revogada</Badge>
                ) : (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm">Revogar</Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Revogar a chave “{key.label}”?</AlertDialogTitle>
                        <AlertDialogDescription>
                          O agente que usa esta chave para de acessar o sistema imediatamente. Não dá para desfazer.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => revoke(key)}>Revogar</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
